// Worker page section: schedules (P2-H02) with pause/unpause and run now (P2-H03).
import { useState } from 'react';
import { Badge } from '@heyroot/ui/primitives/Badge';
import { Button } from '@heyroot/ui/primitives/Button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeaderCell,
  TableRow,
} from '@heyroot/ui/primitives/Table';
import { PERMS } from '../../../../rbac/permissions';
import {
  pauseWorkerSchedule,
  runWorkerScheduleNow,
  unpauseWorkerSchedule,
  useWorkerAction,
  useWorkerSchedules,
  type WorkerScheduleView,
} from '../../../lib/worker-api';
import type { WorkerSectionWeb } from '../sections';
import {
  ActionOutcome,
  PauseBadge,
  PauseForm,
  QueryState,
  SectionHeading,
  SeverityBadge,
  fmtTs,
  useCanOperate,
} from '../worker-ui';

function cadence(s: WorkerScheduleView): string {
  const base = s.cron ?? (s.everySeconds !== null ? `every ${s.everySeconds}s` : '—');
  return `${base} (${s.tz})`;
}

function SchedulesSection() {
  const q = useWorkerSchedules();
  const canOperate = useCanOperate();
  const [pausing, setPausing] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const pause = useWorkerAction((name: string, until: string, reason: string) =>
    pauseWorkerSchedule(name, until, reason),
  );
  const unpause = useWorkerAction((name: string) => unpauseWorkerSchedule(name));
  const runNow = useWorkerAction((name: string) => runWorkerScheduleNow(name));
  const lastError = pause.error ?? unpause.error ?? runNow.error;
  const busy = pause.isPending || unpause.isPending || runNow.isPending;
  const rows = q.data?.schedules ?? [];

  const done = (what: string) => (res: { result: string }) => setSuccess(`${what}: ${res.result}`);

  return (
    <section className="flex flex-col gap-3" aria-label="Schedules">
      <SectionHeading
        title="Schedules"
        description="Next run, last success, failures and skips. A pause counts only until its end time. Run now is offered only for requeue-safe schedules."
      />
      <ActionOutcome error={lastError} success={lastError ? null : success} />
      <QueryState
        isPending={q.isPending}
        isError={q.isError}
        isEmpty={rows.length === 0}
        emptyTitle="No schedules are registered"
        onRetry={() => void q.refetch()}
      >
        <div className="overflow-x-auto">
          <Table dense>
            <TableHead>
              <tr>
                <TableHeaderCell>Schedule</TableHeaderCell>
                <TableHeaderCell>Cadence</TableHeaderCell>
                <TableHeaderCell>Next run</TableHeaderCell>
                <TableHeaderCell>Last success</TableHeaderCell>
                <TableHeaderCell>Failures / skips</TableHeaderCell>
                <TableHeaderCell>Severity</TableHeaderCell>
                <TableHeaderCell>State</TableHeaderCell>
                {canOperate && <TableHeaderCell>Actions</TableHeaderCell>}
              </tr>
            </TableHead>
            <TableBody>
              {rows.map((s) => (
                <TableRow key={s.name}>
                  <TableCell>
                    <div className="text-body-sm-emphasis">{s.name}</div>
                    <div className="text-caption text-muted">
                      {s.jobKind} · {s.lane}
                    </div>
                  </TableCell>
                  <TableCell>{cadence(s)}</TableCell>
                  <TableCell>{fmtTs(s.nextRunAt)}</TableCell>
                  <TableCell>{fmtTs(s.lastSuccessAt)}</TableCell>
                  <TableCell>
                    {s.consecutiveFailures} / {s.consecutiveSkips}
                  </TableCell>
                  <TableCell>
                    <SeverityBadge severity={s.severity} />
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-1 flex-wrap">
                      {!s.enabled && <Badge variant="neutral">disabled</Badge>}
                      {s.retiredAt && <Badge variant="neutral">retired</Badge>}
                      {s.money && <Badge variant="info">money</Badge>}
                      <PauseBadge pausedNow={s.pausedNow} reason={s.pausedReason} />
                    </div>
                  </TableCell>
                  {canOperate && (
                    <TableCell>
                      {pausing === s.name ? (
                        <PauseForm
                          target={s.name}
                          busy={busy}
                          onCancel={() => setPausing(null)}
                          onPause={(until, reason) =>
                            pause.mutate([s.name, until, reason], {
                              onSuccess: done(`Pause ${s.name}`),
                              onSettled: () => setPausing(null),
                            })
                          }
                        />
                      ) : (
                        <div className="flex items-center gap-1">
                          {s.pausedNow ? (
                            <Button
                              size="sm"
                              variant="secondary"
                              disabled={busy}
                              onClick={() =>
                                unpause.mutate([s.name], { onSuccess: done(`Unpause ${s.name}`) })
                              }
                            >
                              Unpause
                            </Button>
                          ) : (
                            <Button
                              size="sm"
                              variant="secondary"
                              disabled={busy}
                              onClick={() => setPausing(s.name)}
                            >
                              Pause
                            </Button>
                          )}
                          <Button
                            size="sm"
                            variant="secondary"
                            disabled={busy || !s.requeueSafe}
                            title={
                              s.requeueSafe
                                ? 'Enqueue one run of this schedule now'
                                : 'Not requeue-safe: run now is refused'
                            }
                            onClick={() =>
                              runNow.mutate([s.name], { onSuccess: done(`Run now ${s.name}`) })
                            }
                          >
                            Run now
                          </Button>
                        </div>
                      )}
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </QueryState>
    </section>
  );
}

export const section: WorkerSectionWeb = {
  key: 'schedules',
  order: 10,
  label: 'Schedules',
  perm: PERMS.WORKER_READ,
  Component: SchedulesSection,
};
